import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import AnnoSelector from "@/components/AnnoSelector";
import AlunniSendBar from "@/components/AlunniSendBar";
import useAlunniSelection from "@/lib/useAlunniSelection";

/**
 * Pagina "Farmaci" (Primaria — settori E e F).
 *
 * Matrice alunni × 10 slot di farmaci (testo libero).
 * Quando si scrive un nome viene salvato con data/ora di compilazione.
 *
 * Richiesta utente 2026-02-17.
 */
const NUM_SLOTS = 8;

const fmtItDateTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch (e) { return ""; }
};

export default function FarmaciAlunniPage() {
  const { annoCorrente } = useAuth();
  const { gestione } = useParams(); // eslint-disable-line no-unused-vars
  const [data, setData] = useState({ anno: null, items: [], num_slots: NUM_SLOTS });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [filterSet, setFilterSet] = useState("");
  const [filterCla, setFilterCla] = useState("");
  const [sortMode, setSortMode] = useState("settore_classe"); // "settore_classe" | "cognome"
  const [soloFarmaci, setSoloFarmaci] = useState(false); // 🆕 Feb 2026: filtro "Solo con farmaci"
  const [editing, setEditing] = useState(null); // {cod, slot, value}
  // 🆕 Feb 2026: import massivo XLSX
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importOverwrite, setImportOverwrite] = useState(false);
  const fileInputRef = React.useRef(null);
  const sel = useAlunniSelection();

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const params = annoCorrente ? { anno: annoCorrente } : {};
      const r = await api.get("/farmaci-alunni/matrice", { params });
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [annoCorrente]);

  const setoriList = useMemo(() => {
    const s = new Set(data.items.map(it => it.settore).filter(Boolean));
    return Array.from(s).sort();
  }, [data.items]);
  const classiList = useMemo(() => {
    const s = new Set(data.items.filter(it => !filterSet || it.settore === filterSet).map(it => it.classe).filter(Boolean));
    return Array.from(s).sort();
  }, [data.items, filterSet]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = data.items.filter(it => {
      if (filterSet && it.settore !== filterSet) return false;
      if (filterCla && it.classe !== filterCla) return false;
      if (q && !(`${it.Cog} ${it.Nom}`.toLowerCase().includes(q) || String(it.Cod).includes(q))) return false;
      // 🆕 Filtro "Solo con farmaci": mostra solo alunni con almeno uno slot popolato
      if (soloFarmaci) {
        const has = Array.isArray(it.farmaci)
          ? it.farmaci.some(f => (f?.nome || "").trim())
          : Object.values(it.farmaci || {}).some(f => (f?.nome || "").trim());
        if (!has) return false;
      }
      return true;
    });
    const cmpStr = (a, b) => (a || "").localeCompare(b || "", "it", { sensitivity: "base" });
    if (sortMode === "cognome") {
      return [...list].sort((a, b) => cmpStr(a.Cog, b.Cog) || cmpStr(a.Nom, b.Nom));
    }
    return [...list].sort((a, b) =>
      cmpStr(a.settore, b.settore) ||
      cmpStr(a.classe, b.classe) ||
      cmpStr(a.Cog, b.Cog) ||
      cmpStr(a.Nom, b.Nom)
    );
  }, [data.items, search, filterSet, filterCla, sortMode, soloFarmaci]);

  const saveCell = async (cod, slot, nome) => {
    const trimmed = (nome || "").trim();
    const prev = data.items.find(it => it.Cod === cod)?.farmaci?.[slot];
    const prevNome = (prev?.nome || "").trim();
    if (trimmed === prevNome) {
      setEditing(null);
      return; // niente da fare
    }
    try {
      const r = await api.put(`/farmaci-alunni/${cod}/slot/${slot}`,
        { nome: trimmed },
        { params: annoCorrente ? { anno: annoCorrente } : {} });
      setData(d => ({
        ...d,
        items: d.items.map(it => {
          if (it.Cod !== cod) return it;
          const newDel = [...(it.farmaci || Array(NUM_SLOTS).fill(null))];
          if (r.data.deleted) {
            newDel[slot] = null;
          } else {
            newDel[slot] = {
              nome: r.data.nome,
              updated_at: r.data.updated_at,
              updated_by: r.data.updated_by,
            };
          }
          return { ...it, farmaci: newDel };
        }),
      }));
      setEditing(null);
    } catch (e) {
      alert("Errore salvataggio: " + (e.response?.data?.detail || e.message));
    }
  };

  // 🆕 Feb 2026: gestisce upload XLSX per import massivo farmaci
  const handleImportXlsx = async (evt) => {
    const f = evt.target.files?.[0];
    evt.target.value = "";
    if (!f) return;
    setImporting(true); setImportResult(null); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const params = {};
      if (annoCorrente) params.anno = annoCorrente;
      if (importOverwrite) params.overwrite = true;
      const r = await api.post("/farmaci-alunni/import-xlsx", fd, {
        params,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(r.data);
      await load();
    } catch (e) {
      setErr("Errore import: " + (e.response?.data?.detail || e.message));
    } finally {
      setImporting(false);
    }
  };

  // 🆕 Feb 2026: label colonna per slot (usata in messaggi/tooltip)
  const SLOT_LABELS = ["Data certificato", "Da PEC a ATS", "Farmaco 1", "Farmaco 2", "Farmaco 3", "Farmaco 4", "Farmaco 5", "Nota"];
  const labelForSlot = (s) => SLOT_LABELS[s] || `Slot ${s + 1}`;

  // 🆕 Feb 2026: duplica un singolo farmaco nell'anno successivo
  const duplicaAnnoSuccessivo = async (cod, slot, nome) => {
    if (!nome) return;
    const annoAtt = data.anno || annoCorrente;
    if (!annoAtt) { alert("Anno scolastico non definito"); return; }
    const annoNext = Number(annoAtt) + 1;
    if (!window.confirm(`Duplicare "${nome}" nell'anno ${annoNext}/${String((annoNext + 1)).slice(-2)}?\n(Verrà scritto sullo stesso slot ${labelForSlot(slot)}, sovrascrivendo se già presente)`)) return;
    try {
      await api.put(`/farmaci-alunni/${cod}/slot/${slot}`, { nome }, { params: { anno: annoNext } });
      alert(`✓ "${nome}" duplicato nell'anno ${annoNext}/${String((annoNext + 1)).slice(-2)} (${labelForSlot(slot)}).`);
    } catch (e) {
      alert("Errore duplicazione: " + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div data-testid="farmaci-ritiro-page" style={{ padding: "12px 20px" }}>
      <div className="sw-page-title">
        <h1>💊 TABELLA FARMACI 2025/26</h1>
        <div className="sw-breadcrumb">Home &gt; TABELLA FARMACI 2025/26</div>
        {/* 🆕 Feb 2026: bottone Importa XLSX */}
        <input ref={fileInputRef} type="file" accept=".xlsx"
          data-testid="farmaci-import-file"
          onChange={handleImportXlsx}
          style={{ display: "none" }} />
        <label style={{
          marginLeft: "auto",
          display: "inline-flex", alignItems: "center", gap: 4,
          fontSize: 10, color: "#666", cursor: "pointer",
        }} title="Se attivo, cancella le farmaci esistenti dell'alunno prima di importare quelle nuove (per gli alunni presenti nel file)">
          <input type="checkbox" checked={importOverwrite}
            data-testid="farmaci-import-overwrite"
            onChange={e => setImportOverwrite(e.target.checked)} />
          Sovrascrivi
        </label>
        <button onClick={() => fileInputRef.current?.click()}
          disabled={importing}
          data-testid="farmaci-import-btn"
          title="Importa un file .xlsx con schema Segremat Web (id alunno in colonna 3, nomi farmaci da colonna 5)"
          style={{ marginLeft: 6, padding: "7px 14px", background: importing ? "#9e9e9e" : "#00695c", color: "white", border: "none", borderRadius: 3, fontSize: 11, fontWeight: 700, cursor: importing ? "wait" : "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
          {importing ? "⏳ Import…" : "📥 Importa XLSX"}
        </button>
        <button onClick={() => {
            const base = process.env.REACT_APP_BACKEND_URL;
            const annoQ = annoCorrente ? `?anno=${annoCorrente}` : "";
            window.open(`${base}/api/farmaci-alunni/stampa${annoQ}`, "_blank", "noopener,noreferrer");
          }}
          data-testid="farmaci-stampa-btn"
          title="Stampa l'elenco completo delle farmaci ritiro (PDF A3 orizzontale)"
          style={{ marginLeft: 6, padding: "7px 14px", background: "#4a148c", color: "white", border: "none", borderRadius: 3, fontSize: 11, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
          🖨 Stampa elenco
        </button>
      </div>

      {/* 🆕 Feb 2026: banner risultato import XLSX */}
      {importResult && (
        <div style={{ margin: "10px 0", padding: 10, background: "#e8f5e9", border: "1px solid #81c784", borderRadius: 4, fontSize: 12 }}>
          <div style={{ fontWeight: 800, color: "#1b5e20", marginBottom: 4 }}>
            ✓ Import completato:
            {" "}<b>{importResult.slot_importati}</b> slot farmaco scritti su
            {" "}<b>{importResult.totale_righe_valide}</b> alunni.
            <button onClick={() => setImportResult(null)} style={{ marginLeft: 12, background: "transparent", border: "none", color: "#1b5e20", cursor: "pointer", fontWeight: 700 }}>✕ chiudi</button>
          </div>
          {importResult.count_alunno_non_trovato > 0 && (
            <div style={{ color: "#bf360c" }}>
              ⚠ <b>{importResult.count_alunno_non_trovato}</b> alunni presenti nel file ma non trovati nell&apos;anno selezionato.
            </div>
          )}
          {importResult.count_troncati > 0 && (
            <div style={{ color: "#e65100" }}>
              ⚠ <b>{importResult.count_troncati}</b> alunni avevano più di 7 nomi: solo i primi 7 sono stati importati (limite slot).
            </div>
          )}
          {importResult.righe_senza_nomi > 0 && (
            <div style={{ color: "#666" }}>
              {importResult.righe_senza_nomi} righe senza nomi farmaci (ignorate).
            </div>
          )}
          {importResult.count_errori > 0 && (
            <div style={{ color: "#c62828" }}>
              ✗ <b>{importResult.count_errori}</b> errori (i primi 50 sono nella response API).
            </div>
          )}
        </div>
      )}

      <div className="sw-card">
        <div className="sw-card-body">
          <div className="sw-filter-row" style={{ flexWrap: "wrap", gap: 8 }}>
            <AnnoSelector testId="farmaci-anno-attivo" />
            <span className="sw-filter-label" style={{ marginLeft: 8 }}>Cerca:</span>
            <input data-testid="farmaci-search" type="text" placeholder="cognome, nome, cod…"
              value={search} onChange={e => setSearch(e.target.value)}
              className="sw-filter-input" style={{ width: 200 }} />
            <span className="sw-filter-label" style={{ marginLeft: 8 }}>Settore:</span>
            <select className="sw-filter-select" value={filterSet}
              onChange={e => { setFilterSet(e.target.value); setFilterCla(""); }}
              data-testid="farmaci-filter-settore" style={{ minWidth: 100 }}>
              <option value="">Tutti i settori</option>
              {setoriList.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <span className="sw-filter-label" style={{ marginLeft: 8 }}>Classe:</span>
            <select className="sw-filter-select" value={filterCla}
              onChange={e => setFilterCla(e.target.value)} style={{ minWidth: 100 }}>
              <option value="">Tutte</option>
              {classiList.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <span className="sw-filter-label" style={{ marginLeft: 8 }}>Mostra:</span>
            <select data-testid="farmaci-filter-attivi" value={soloFarmaci ? "attivi" : ""}
              onChange={e => setSoloFarmaci(e.target.value === "attivi")}
              title="Attivi = alunni con almeno un farmaco/certificato registrato"
              className="sw-filter-select"
              style={{ minWidth: 110, background: soloFarmaci ? "#fff59d" : "white", fontWeight: soloFarmaci ? 700 : 400 }}>
              <option value="">Tutti</option>
              <option value="attivi">Solo con dati inseriti</option>
            </select>
            {(search || filterSet || filterCla || soloFarmaci) && (
              <button className="sw-filter-btn" onClick={() => { setSearch(""); setFilterSet(""); setFilterCla(""); setSoloFarmaci(false); }}>
                Pulisci
              </button>
            )}
            <span className="sw-filter-label" style={{ marginLeft: 8 }}>Ordina:</span>
            <select className="sw-filter-select" value={sortMode} onChange={e => setSortMode(e.target.value)}
              data-testid="farmaci-sort-select" style={{ minWidth: 180 }}>
              <option value="settore_classe">Settore · Classe · Cognome Nome</option>
              <option value="cognome">Cognome · Nome</option>
            </select>
            {/* 🆕 Feb 2026: filtro "Solo con farmaci" spostato nel select "Mostra" */}
            <span style={{ marginLeft: "auto", fontSize: 11, color: "#444" }}>
              <b>{filtered.length}</b> alunni · <b>{NUM_SLOTS}</b> slot farmaco per alunno
            </span>
          </div>
          <div style={{ marginTop: 6, fontSize: 11, color: "#555", lineHeight: 1.4 }}>
            Vista di <b>tutti</b> gli alunni della scuola. Clicca su una cella per inserire o modificare il
            nome del farmaco; data e ora di compilazione vengono registrate automaticamente sotto al nome.
          </div>
        </div>
      </div>

      {err && <div style={{ padding: 10, background: "#ffebee", color: "#c62828", border: "1px solid #ef9a9a", margin: "8px 0", fontSize: 12 }}>{err}</div>}

      <div className="sw-card" style={{ marginTop: 8 }}>
        <div style={{ overflow: "auto", maxHeight: "75vh" }}>
          <AlunniSendBar selected={sel.selected} onClear={sel.clear}
            label={`Farmaci ${data.anno ? `${data.anno}/${String((data.anno||0)+1).slice(-2)}` : ""}`}
            anno={data.anno} />
          <table className="sw-table" style={{ width: "100%", tableLayout: "fixed" }}>
            <thead className="thead-blue">
              <tr>
                <th style={{ width: 28, textAlign: "center" }}>
                  <input type="checkbox" data-testid="farmaci-selall"
                    title="Seleziona/Deseleziona tutti i visibili"
                    checked={filtered.length > 0 && filtered.every(it => sel.isSelected(it.Cod))}
                    onChange={(e) => e.target.checked ? sel.selectAll(filtered.map(it => it.Cod)) : sel.deselectAll(filtered.map(it => it.Cod))} />
                </th>
                <th style={{ width: 32, textAlign: "center" }}>#</th>
                <th style={{ width: 52, textAlign: "center" }}>Cod</th>
                <th style={{ width: 150 }}>Cognome Nome</th>
                <th style={{ width: 36, textAlign: "center" }}>S.</th>
                <th style={{ width: 36, textAlign: "center" }}>Cl.</th>
                {(() => {
                  // 🆕 Feb 2026: header colonne personalizzato per Farmaci:
                  //   slot 0 → "Data certificato", slot 1 → "PEC ATS", slot 2..6 → "Farmaco 1..5"
                  const HEADERS = ["Data certificato", "Da PEC a ATS", "Farmaco 1", "Farmaco 2", "Farmaco 3", "Farmaco 4", "Farmaco 5", "Nota"];
                  return Array.from({ length: NUM_SLOTS }, (_, i) => (
                    <th key={i} style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 10, fontWeight: 700 }}>{HEADERS[i] || `Slot ${i + 1}`}</div>
                    </th>
                  ));
                })()}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6 + NUM_SLOTS} className="sw-loading">Caricamento…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6 + NUM_SLOTS} className="sw-empty"><p>Nessun alunno trovato nei settori E/F per l&apos;anno selezionato</p></td></tr>
              ) : filtered.map((it, i) => (
                <tr key={it.Cod} data-testid={`farmaci-row-${it.Cod}`}
                    style={{ background: sel.isSelected(it.Cod) ? "#f3e5f5" : undefined }}>
                  <td style={{ textAlign: "center" }}>
                    <input type="checkbox" data-testid={`farmaci-sel-${it.Cod}`}
                      checked={sel.isSelected(it.Cod)} onChange={() => sel.toggle(it.Cod)} />
                  </td>
                  <td style={{ color: "#888", textAlign: "center", fontSize: 11 }}>{i + 1}</td>
                  <td style={{ textAlign: "center", fontFamily: "Consolas", fontSize: 11 }}>{it.Cod}</td>
                  <td style={{ fontWeight: 600, fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={`${it.Cog} ${it.Nom}`}>{it.Cog} {it.Nom}</td>
                  <td style={{ textAlign: "center", fontWeight: 600, fontSize: 11 }}>{it.settore || "-"}</td>
                  <td style={{ textAlign: "center", fontWeight: 600, fontSize: 11 }}>{it.classe || "-"}</td>
                  {Array.from({ length: NUM_SLOTS }, (_, slot) => {
                    const d = it.farmaci?.[slot];
                    const isEditing = editing && editing.cod === it.Cod && editing.slot === slot;
                    return (
                      <td key={slot} style={{ padding: 2, verticalAlign: "top", position: "relative" }}>
                        {isEditing ? (
                          <input
                            data-testid={`farmaci-input-${it.Cod}-${slot}`}
                            type="text"
                            autoFocus
                            value={editing.value}
                            onChange={(e) => setEditing(s => ({ ...s, value: e.target.value }))}
                            onBlur={() => saveCell(it.Cod, slot, editing.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") { e.target.blur(); }
                              else if (e.key === "Escape") { setEditing(null); }
                            }}
                            style={{
                              width: "100%", padding: "3px 5px", fontSize: 11,
                              border: "1.5px solid #0d47a1", borderRadius: 3,
                              boxSizing: "border-box", outline: "none",
                            }}
                            placeholder="Nome"
                          />
                        ) : (
                          <button
                            onClick={() => setEditing({ cod: it.Cod, slot, value: d?.nome || "" })}
                            data-testid={`farmaci-cell-${it.Cod}-${slot}`}
                            title={d?.nome ? `${d.nome}${d?.updated_at ? ` — inserito il ${fmtItDateTime(d.updated_at)}` : ""}${d?.updated_by ? ` da ${d.updated_by}` : ""}` : "Clicca per inserire il nome del farmaco"}
                            style={{
                              width: "100%", minHeight: 34, padding: "3px 5px",
                              background: d?.nome ? "#2e7d32" : "transparent",
                              border: `1px dashed ${d?.nome ? "#1b5e20" : "#cfd8dc"}`,
                              borderRadius: 3,
                              textAlign: "left", cursor: "text",
                              display: "flex", flexDirection: "column", justifyContent: "center",
                              alignItems: "flex-start", overflow: "hidden",
                            }}>
                            {d?.nome ? (
                              <>
                                <span style={{ fontSize: 11, fontWeight: 700, color: "white", lineHeight: 1.15, width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {d.nome}
                                </span>
                                <span style={{ fontSize: 9, color: "#c8e6c9", lineHeight: 1.15, marginTop: 1, fontStyle: "italic" }}>
                                  {fmtItDateTime(d.updated_at)}
                                </span>
                              </>
                            ) : (
                              <span style={{ fontSize: 10, color: "#90a4ae", fontStyle: "italic" }}>+ aggiungi</span>
                            )}
                          </button>
                        )}
                        {/* 🆕 Feb 2026: mini-bottone "↗" duplica farmaco nell'anno successivo (solo se c'è un nome) */}
                        {d?.nome && !isEditing && (
                          <button
                            onClick={(e) => { e.stopPropagation(); duplicaAnnoSuccessivo(it.Cod, slot, d.nome); }}
                            data-testid={`farmaci-dup-next-${it.Cod}-${slot}`}
                            title={`Duplica "${d.nome}" nell'anno successivo (stesso slot ${labelForSlot(slot)})`}
                            style={{
                              position: "absolute", top: 3, right: 3,
                              background: "rgba(255,255,255,0.25)", color: "white",
                              border: "1px solid rgba(255,255,255,0.5)", borderRadius: 3,
                              padding: "0 4px", fontSize: 10, fontWeight: 800,
                              lineHeight: 1.3, cursor: "pointer",
                            }}>
                            ↗+1
                          </button>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
